// ======================================================================
// FILE:
// src/modules/finance/transactions/payment_voucher/services/paymentVoucherService.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Payment Voucher
//
// PURPOSE:
// Frontend API Client for Finance Payment Voucher.
//
// RESPONSIBILITIES
// - Payment Voucher Header CRUD
// - Payment Voucher Workflow
// - No accounting logic
// - No React code
// - No UI logic
// - No Toast
// - No Navigation
// - No State Management
//
// AUTHOR:
// DizyPro ERP
// ======================================================================

import axiosClient from  "../../../../../api/axiosClient";

// ======================================================================
// API BASE URL
// ======================================================================

const BASE_URL =
    "/api/finance/payment-vouchers";

// ======================================================================
// LIST PAYMENT VOUCHERS
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * LIST PAYMENT VOUCHERS
 * ----------------------------------------------------------------------
 *
 * Backend:
 * GET /api/finance/payment-vouchers
 *
 * Supported filters are passed directly to the backend.
 *
 * Example:
 *
 * {
 *     status: "DRAFT",
 *     payee_type: "VENDOR",
 *     payment_mode: "BANK_TRANSFER",
 *     search: "ABC",
 *     date_from: "2026-04-01",
 *     date_to: "2026-04-30"
 * }
 *
 */

async function listPaymentVouchers(
    filters = {}
) {

    const response =
        await axiosClient.get(
            BASE_URL,
            {
                params: filters,
            }
        );

    const data =
        response?.data?.data;

    /*
     * Keep the service flexible because the backend currently
     * returns the Payment Voucher collection directly.
     */

    if (Array.isArray(data)) {

        return {
            rows: data,
            summary: {},
            pagination: {},
        };

    }

    return {

        rows:
            data?.rows ||
            data?.items ||
            data?.data ||
            [],

        summary:
            data?.summary ||
            {},

        pagination:
            data?.pagination ||
            {},

    };

}

// ======================================================================
// GET PAYMENT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * GET PAYMENT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * GET /api/finance/payment-vouchers/{id}
 *
 */

async function getPaymentVoucher(
    paymentVoucherId
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/${paymentVoucherId}`
        );

    return response.data.data;

}

// ======================================================================
// CREATE PAYMENT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * CREATE PAYMENT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * POST /api/finance/payment-vouchers
 *
 * The page is responsible for preparing the payload.
 * The service simply sends it to the backend.
 *
 */

async function createPaymentVoucher(
    payload
) {

    const response =
        await axiosClient.post(
            BASE_URL,
            payload
        );

    return response.data.data;

}

// ======================================================================
// UPDATE PAYMENT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * UPDATE PAYMENT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * PUT /api/finance/payment-vouchers/{id}
 *
 * Only Draft Payment Vouchers can be updated.
 * Backend remains authoritative for this rule.
 *
 */

async function updatePaymentVoucher(
    paymentVoucherId,
    payload
) {

    const response =
        await axiosClient.put(
            `${BASE_URL}/${paymentVoucherId}`,
            payload
        );

    return response.data.data;

}

// ======================================================================
// DELETE PAYMENT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * DELETE PAYMENT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * DELETE /api/finance/payment-vouchers/{id}
 *
 * Backend performs soft delete.
 *
 */

async function deletePaymentVoucher(
    paymentVoucherId
) {

    const response =
        await axiosClient.delete(
            `${BASE_URL}/${paymentVoucherId}`
        );

    return response.data;

}

// ======================================================================
// SUBMIT PAYMENT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * SUBMIT PAYMENT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * POST /api/finance/payment-vouchers/{id}/submit
 *
 */

async function submitPaymentVoucher(
    paymentVoucherId,
    companyId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${paymentVoucherId}/submit`,
            {
                company_id: companyId
            }
        );

    return response.data.data;

}

// ======================================================================
// APPROVE PAYMENT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * APPROVE PAYMENT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * POST /api/finance/payment-vouchers/{id}/approve
 *
 */

async function approvePaymentVoucher(
    paymentVoucherId,
    companyId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${paymentVoucherId}/approve`,
            {
                company_id: companyId
            }
        );

    return response.data.data;

}

// ======================================================================
// POST PAYMENT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * POST PAYMENT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * POST /api/finance/payment-vouchers/{id}/post
 *
 * IMPORTANT:
 * Actual accounting journal creation/posting remains a backend
 * Finance Runtime responsibility.
 *
 */

async function postPaymentVoucher(
    paymentVoucherId,
    companyId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${paymentVoucherId}/post`,
            {
                company_id: companyId
            }
        );

    return response.data.data;

}
// ======================================================================
// EXPORT
// ======================================================================

const paymentVoucherService = {

    // --------------------------------------------------------------
    // Payment Voucher Header
    // --------------------------------------------------------------

    listPaymentVouchers,

    getPaymentVoucher,

    createPaymentVoucher,

    updatePaymentVoucher,

    deletePaymentVoucher,

    // --------------------------------------------------------------
    // Workflow
    // --------------------------------------------------------------

    submitPaymentVoucher,

    approvePaymentVoucher,

    postPaymentVoucher,

};

export default paymentVoucherService;